import Joi from '@hapi/joi'
import 'joi-extract-type'
import * as Sentry from '@sentry/browser'

//                    __ _
//                   / _(_)
//    ___ ___  _ __ | |_ _  __ _
//   / __/ _ \| '_ \|  _| |/ _` |
//  | (_| (_) | | | | | | | (_| |
//   \___\___/|_| |_|_| |_|\__, |
//                          __/ |
//                         |___/

// Region
const regionUserFeaturesSchema = Joi.object({
  signup: Joi.bool().optional(),
}).unknown(true)

const workstationsFeaturesSchema = Joi.object({
  start: Joi.bool().optional(),
}).unknown(true)

const drivesFeaturesSchema = Joi.object({
  change_size: Joi.bool().optional(),
  view: Joi.bool().optional(),
}).unknown(true)

const resourcesFeaturesSchema = Joi.object({
  workstations: workstationsFeaturesSchema.optional(),
  drives: drivesFeaturesSchema.optional(),
}).unknown(true)

const regionFeaturesSchema = Joi.object({
  user: regionUserFeaturesSchema.optional(),
  resources: resourcesFeaturesSchema.optional(),
}).unknown(true)

const regionSchema = Joi.object({
  code: Joi.string().required(), // perhaps a string literal?
  name: Joi.string().optional(), // currently not used, therefore optional
  disabled: Joi.bool().optional(),
  features: regionFeaturesSchema.optional().allow(null),
}).unknown(true)

// Bundle
const bundleSingleWorkstationDetailSchema = Joi.object({
  price: Joi.string().required(),
}).unknown(true)

const bundleWorkstationDetailSetSchema = Joi.object({
  basic: bundleSingleWorkstationDetailSchema.required(),
  medium: bundleSingleWorkstationDetailSchema.required(),
  advanced: bundleSingleWorkstationDetailSchema.required(),
  turbo: bundleSingleWorkstationDetailSchema.required(),
}).unknown(true)

const bundleFeatureSchema = Joi.object({
  icon: Joi.string().required(),
  text: Joi.string().required(),
}).unknown(true)

const bundleLimitsSchema = Joi.object({
  drives: Joi.number().positive().required(),
  workstations: Joi.number().positive().required(),
}).unknown(true)

const trialInfoSchema = Joi.object({
  days: Joi.number().positive().required(),
  price_usd: Joi.string().required(),
}).unknown(true)

const bundleSchema = Joi.object({
  active: Joi.bool().optional().allow(null),
  name: Joi.string().required(),
  codename: Joi.string().required(), // shouldn't be a string literal?
  price_usd_per_month: Joi.string().optional(), // perhaps validate it includes a dot?
  month: Joi.any().optional(),
  year: Joi.any().optional(),

  workstation_details: bundleWorkstationDetailSetSchema.optional().allow(null),
  features: Joi.array().items(bundleFeatureSchema).required(),
  badge: Joi.string().optional().allow(null, ''),
  limits: bundleLimitsSchema.optional().allow(null), // enterpraise has no limits ;)
  selectable: Joi.bool().optional(),
  email_only: Joi.bool().required(),
  trial_info: trialInfoSchema.optional().allow(null),
  available_hours_per_workstation: Joi.number().optional().allow(null), // number rather than positive number to allow 0
}).unknown(true)

const workstationTypeSpecsSchema = Joi.object({
  cpu: Joi.string().required(),
  ram: Joi.string().required(),
  gpu: Joi.string().required(),
}).unknown(true)

const InstanceTypeDetailsSchema = Joi.object({
  instance_type: Joi.string(),
  instance_type_class: Joi.string(),
})

const workstationTypeSchema = Joi.object({
  selectable: Joi.bool().required(),
  codename: Joi.string().required(),
  name: Joi.string().required(),
  price_per_hour_usd: Joi.string().required(),
  root_volume_size: Joi.number().positive().required(),
  specs: workstationTypeSpecsSchema.required(),
  best_for: Joi.array().items(Joi.string()).optional(),
  aws: InstanceTypeDetailsSchema.required(),
}).unknown(true)
export type ApiWorkstationType = Joi.extractType<typeof workstationTypeSchema>

const workstationTypesSchema = Joi.array().items(workstationTypeSchema).required()

export const configResponseSchema = Joi.object({
  region: regionSchema.required(),
  bundles: Joi.array().items(bundleSchema).required(),
  workstation_types: workstationTypesSchema.required(),
}).unknown(true)

export type ApiConfig = Joi.extractType<typeof configResponseSchema>

//   _ _       _
//  | (_)     | |
//  | |_ _ __ | | _____
//  | | | '_ \| |/ / __|
//  | | | | | |   <\__ \
//  |_|_|_| |_|_|\_\___/

const userSharedLinkSchema = Joi.object({
  sharer_uuid: Joi.string().required(),
  drive_uuid: Joi.string().required(),
  hash: Joi.string().required(),
  status: Joi.string().valid('active', 'expired', 'deleted').required(), // shouldn't be a literal?
  created: Joi.number().positive().required(),
  title: Joi.string().optional().allow('', null), // The title field is always there
}).unknown(true)

export const listSharedLinksSchema = Joi.array().items(userSharedLinkSchema).required()

export type ApiListSharedLinks = Joi.extractType<typeof listSharedLinksSchema>

export const createSharedLinkSchema = Joi.object({
  hash: Joi.string().required(),
}).unknown(true)

export type ApiCreateSharedLink = Joi.extractType<typeof createSharedLinkSchema>

//   _                                     _
//  | |                                   | |
//  | |_ _ __ __ _ _ __  ___  ___ ___   __| | ___
//  | __| '__/ _` | '_ \/ __|/ __/ _ \ / _` |/ _ \
//  | |_| | | (_| | | | \__ \ (_| (_) | (_| |  __/
//   \__|_|  \__,_|_| |_|___/\___\___/ \__,_|\___|

const transcodingJobSchema = Joi.object({
  owner_id: Joi.string().required(),
  uuid: Joi.string().required(),
  status: Joi.string()
    .valid(
      'submitted',
      'progressing',
      'probing',
      'transcoding',
      'uploading',
      'complete',
      'error',
      'cancelled',
    )
    .required(),
  path: Joi.string().required(),
  progress: Joi.number().greater(0).less(100).required(),
  drive_uuid: Joi.string().required(),
  created: Joi.number().positive(),
  error_message: Joi.string().optional().allow(null),
}).unknown(true)

export type ApiTranscodingJob = Joi.extractType<typeof transcodingJobSchema>

export const listTranscodingJobsSchema = Joi.object({
  user_jobs: Joi.array().items(transcodingJobSchema).required(),
}).unknown(true)

//    _
//   | |
//   | |_ ___  __ _ _ __ ___
//   | __/ _ \/ _` | '_ ` _ \
//   | ||  __/ (_| | | | | | |
//    \__\___|\__,_|_| |_| |_|

const inviteSchema = Joi.object({
  uuid: Joi.string().required(),
  invitee_email: Joi.string().required(),
  status: Joi.string().valid('pending', 'accepted', 'cancelled', 'expired').required(),
  team_uuid: Joi.string().required(),
  display_name: Joi.string().optional().allow(''),
  photo_url: Joi.string().optional().allow(''),
  created: Joi.number().positive().required(),
}).unknown(true)

export type ApiInvite = Joi.extractType<typeof inviteSchema>

export const getInvitesSchema = Joi.object({
  user_invites: Joi.array().items(inviteSchema).required(),
}).unknown(true)

const memberSchema = Joi.object({
  uuid: Joi.string().required(),
  member_uuid: Joi.string().required(),
  role: Joi.string().valid('guest', 'owner').required(),
  photo_url: Joi.string().optional().allow(''),
  display_name: Joi.string().optional().allow(''),
  email: Joi.string().required(),
  created: Joi.number().positive().required(),
}).unknown(true)

const teamSchema = Joi.object({
  uuid: Joi.string().required(),
  members: Joi.array().items(memberSchema).required(),
}).unknown(true)

export type ApiTeam = Joi.extractType<typeof teamSchema>

export const getTeamsSchema = Joi.object({
  user_teams: Joi.array().items(teamSchema).required(),
}).unknown(true)

/**
 * Assert data's shape is in accordance with schema provided
 * @param data piece of data (usually JSON) to be validated
 * @param schema against which the check should be performed
 */
export function captureInvalidSchemaOnSentry(data: any, schema: Joi.Schema): void {
  try {
    Joi.assert(data, schema)
  } catch (err) {
    Sentry.captureException(err)
  }
}
